import type { AssetInput, DraftContentInput } from './graphqlTypes'
import type { OmniAsset, OmniDraft } from '../store/postSlice/types'
import { mapDocument, mapGif, mapImage, mapVideo } from './mapAssets'

/**
 * Maps a single OmniAsset to its AssetInput @oneOf variant.
 *
 * GIF has no dedicated wire variant, so it collapses to an image (same
 * as the per-channel mapMediaAssets path). `canTag` defaults to false for
 * the channel-less omni call site (no channel selected, so no per-channel
 * user-tag context); the per-channel path passes the target channel's own
 * capability.
 *
 * `includeThumbnailUrl` defaults to `true` for this call site's own use
 * (a `DraftContentInput`, where the server has no fallback to `url` — see
 * mapImage); channelVariantToCreatePostInput.ts's `mapChannelVariantAssets`/
 * `mapChannelVariantThreadItemAssets` — reused for both post creation and
 * edits (see channelVariantToEditPostInput.ts) — pass `false`.
 */
export function omniAssetToAssetInput(
  asset: Exclude<OmniAsset, { kind: 'link' }>,
  canTag = false,
  { includeThumbnailUrl = true }: { includeThumbnailUrl?: boolean } = {},
): AssetInput {
  switch (asset.kind) {
    case 'image':
      return { image: mapImage(asset.image, canTag, { includeThumbnailUrl }) }
    case 'gif':
      // GIF has no AssetInput wire variant; collapse to image like the per-channel path.
      return { image: mapGif(asset.gif) }
    case 'video':
      return { video: mapVideo(asset.video) }
    case 'document':
      return { document: mapDocument(asset.document) }
  }
}

/**
 * Maps the channel-less OmniDraft to a DraftContentInput for
 * createContentItemDraft. `urls`/`unshortenedUrls` are client-internal
 * (link preview, char count) and are intentionally excluded — URLs live
 * inline in `text` on the wire. Link assets are dropped too: a ContentItem
 * draft has no link asset variant, so the URL stays inline in the text and
 * the preview is chosen when the draft gets a channel.
 */
export function mapToDraftContentInput(omni: OmniDraft): DraftContentInput {
  return {
    text: omni.text,
    assets: omni.assets
      .filter((asset) => asset.kind !== 'link')
      .map((asset) => omniAssetToAssetInput(asset)),
    aiAssisted: omni.aiAssisted,
  }
}
